import React, { useRef, useState, useEffect } from "react";
import { CHAINS, toConfidentialSymbol, toConfidentialName } from "../config/chains";

const MONO = "'JetBrains Mono', monospace";
const SANS = "'Space Grotesk', sans-serif";
const SERIF = "'Instrument Serif', serif";

const fmt = (n) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Balances modal (desktop). Values straight from the design reference
 * (usernames-desktop.dc.html renderVals()) — see handoff/BALANCES.md.
 *
 * Deliberate departures from the reference (this is the real multi-chain
 * app, not the mock's fixed "Arbitrum" state):
 *  - The external-deposit block's network warning names the chain and
 *    native symbol the user actually selected, not a hardcoded "Arbitrum
 *    One" / "ETH" — the whole point of that block is not leaving the
 *    network implicit, and a static "Arbitrum" would be wrong (and unsafe
 *    advice) on any other chain.
 *  - Only the currently-selected confidential token is listed (decrypting
 *    both would add a slow second decrypt on every open).
 */
export default function BalancesModal({
  onClose,
  address,
  chainKey,
  chainName,
  nativeSymbol,
  nativeBalance,
  gasState, // 'ok' | 'low' | 'empty' | null (null = still loading)
  wallet, // { [tokenId]: null | token shape + balance } from useBalances
  activeTokenSymbol, // currentTokenData?.symbol — which confidential row to show
  confidentialBalance, // raw (×100) value for the active token, or null
  onDeposit, // (tokenId) => void — open deposit when wallet has tokens
}) {
  const [gasDepositOpen, setGasDepositOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);

  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
  }, []);

  const isOk = (gasState || "ok") === "ok"; // unresolved reads as funded, not alarming

  const gasAmountText = nativeBalance != null ? nativeBalance.toFixed(4) : "…";
  const gasActionLabel = gasDepositOpen ? "Hide address" : isOk ? "Top up gas" : "Deposit from external wallet";

  const handleCopyAddress = () => {
    if (!address) return;
    if (navigator.clipboard) navigator.clipboard.writeText(address).catch(() => {});
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  const chain = chainKey ? CHAINS[chainKey] : null;
  const isTestnet = chain?.network === "testnet";
  const tokenKeys = Object.keys(chain?.tokens || {});
  const confidentialRows = tokenKeys
    .map((key) => chain?.tokens?.[key])
    .filter(Boolean)
    .filter((t) => t.active !== false)
    .filter((t) => !!activeTokenSymbol && t.symbol === activeTokenSymbol)
    .map((t) => {
      const known = confidentialBalance != null ? confidentialBalance / 100 : null;
      return {
        symbol: toConfidentialSymbol(t.symbol, t.name),
        name: toConfidentialName(t.symbol, t.name),
        icon: t.icon,
        known,
      };
    });

  const walletRows = tokenKeys
    .map((key) => (wallet?.[key] ? { ...wallet[key], tokenId: key } : null))
    .filter(Boolean);

  const handleGetSome = () => {
    // Zero wallet balance: acquire tokens first. Deposit only moves what's
    // already in the wallet into confidential — opening it here is a dead end.
    // Mainnet has no faucet page; leave the CTA off (handled in the row render).
    const opened = window.open("/faucets.html", "_blank", "noopener,noreferrer");
    if (opened) opened.opener = null;
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(255,255,255,0.86)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "78px 0 60px",
        overflowY: "auto", // the deposit block can push this past the viewport
        zIndex: 10010, // same overlay family as .token-selector-overlay
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: 520,
          background: "#FFFFFF",
          border: "1px solid #E6E6E1",
          boxShadow: "0 30px 70px -40px rgba(20,22,24,0.28)",
        }}
      >
        {/* header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            padding: "22px 26px 18px",
            borderBottom: "1px solid #F0F0EC",
          }}
        >
          <div>
            <div style={{ fontFamily: SERIF, fontSize: 26, lineHeight: 1.1 }}>Balances</div>
            <div style={{ fontFamily: MONO, fontSize: 10.5, color: "#8E8E88", paddingTop: 5 }}>{address}</div>
          </div>
          <div
            onClick={onClose}
            style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.04em", color: "#9A9A94", cursor: "pointer" }}
          >
            Close
          </div>
        </div>

        {/* gas panel */}
        <div style={{ padding: "20px 26px 22px", borderBottom: "1px solid #F0F0EC", background: "#FFFFFF" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.05em", color: "#8E8E88" }}>
                Gas · {chainName || "—"}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, paddingTop: 8 }}>
                <div style={{ fontFamily: SERIF, fontSize: 38, lineHeight: 1 }}>{gasAmountText}</div>
                <div style={{ fontFamily: MONO, fontSize: 11.5, color: "#6E6E68" }}>{nativeSymbol}</div>
              </div>
            </div>
            <div style={{ flex: "none" }}>
              <div
                onClick={() => setGasDepositOpen((v) => !v)}
                style={{
                  fontFamily: SANS,
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "9px 16px",
                  borderRadius: 999,
                  cursor: "pointer",
                  ...(isOk
                    ? { border: "1px solid #E6E6E1", color: "#16181A", background: "#FFFFFF" }
                    : { border: "1px solid #16181A", background: "#16181A", color: "#FFFFFF" }),
                }}
              >
                {gasActionLabel}
              </div>
            </div>
          </div>

          {gasDepositOpen && (
            <div style={{ marginTop: 18, border: "1px solid #E6E6E1", background: "#FFFFFF" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "13px 16px",
                  borderBottom: "1px solid #F0F0EC",
                  background: "#FDFBF5",
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    flex: "none",
                    borderRadius: "50%",
                    background: chain?.icon ? `url(${chain.icon}) center/contain no-repeat` : "#E6E6E1",
                  }}
                />
                <div style={{ fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: "#16181A" }}>
                  Send {nativeSymbol} on {chainName || "this network"} only
                </div>
              </div>
              <div style={{ padding: "14px 16px 0", fontFamily: MONO, fontSize: 10.5, lineHeight: 1.75, color: "#6E6E68" }}>
                Anything sent on another network, or any token other than {nativeSymbol}, is unrecoverable.
              </div>
              <div style={{ padding: "14px 16px 16px" }}>
                <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.05em", color: "#9A9A94", paddingBottom: 7 }}>
                  Your address
                </div>
                <div
                  onClick={handleCopyAddress}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    border: "1px solid #E6E6E1",
                    padding: "12px 14px",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontFamily: MONO, fontSize: 11.5, lineHeight: 1.6, wordBreak: "break-all", color: "#16181A" }}>
                    {address}
                  </div>
                  <div style={{ flex: "none", fontFamily: MONO, fontSize: 10, letterSpacing: "0.04em", color: "#3E8FCC" }}>
                    {copied ? "Copied" : "Copy"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* confidential balances */}
        <div style={{ padding: "18px 26px 6px" }}>
          <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.05em", color: "#9A9A94" }}>On Stabletrust Pay</div>
        </div>
        {confidentialRows.map((row) => {
          const shown = row.known != null;
          const value = shown ? fmt(row.known) : "…";
          return (
            <div
              key={row.symbol}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "13px 26px", borderTop: "1px solid #F5F5F1" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <div style={{ width: 28, height: 28, flex: "none", borderRadius: "50%", background: `url(${row.icon}) center/contain no-repeat` }} />
                <div>
                  <div style={{ fontFamily: SANS, fontSize: 14.5, fontWeight: 500 }}>{row.symbol}</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: "#9A9A94", paddingTop: 2 }}>{row.name}</div>
                </div>
              </div>
              <div
                style={
                  shown
                    ? { fontFamily: SERIF, fontSize: 21, color: "#16181A", flex: "none" }
                    : { fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.05em", color: "#A5A5A0", flex: "none" }
                }
              >
                {value}
              </div>
            </div>
          );
        })}

        {/* wallet balances */}
        <div style={{ padding: "20px 26px 6px", borderTop: "1px solid #F0F0EC", marginTop: 8 }}>
          <div style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: "0.05em", color: "#9A9A94" }}>In your wallet</div>
        </div>
        {walletRows.map((w) => (
          <div
            key={w.symbol}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "13px 26px", borderTop: "1px solid #F5F5F1" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
              <div style={{ width: 28, height: 28, flex: "none", borderRadius: "50%", background: `url(${w.icon}) center/contain no-repeat` }} />
              <div>
                <div style={{ fontFamily: SANS, fontSize: 14.5, fontWeight: 500 }}>{w.symbol}</div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: "#9A9A94", paddingTop: 2 }}>{w.name}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flex: "none" }}>
              <div style={{ fontFamily: SERIF, fontSize: 21 }}>{w.balance != null ? fmt(w.balance) : "…"}</div>
              {w.balance > 0 ? (
                <div
                  onClick={() => onDeposit?.(w.tokenId)}
                  style={{
                    fontFamily: MONO,
                    fontSize: 10,
                    letterSpacing: "0.04em",
                    color: "#3E8FCC",
                    cursor: "pointer",
                    minWidth: 52,
                    textAlign: "right",
                  }}
                >
                  Deposit
                </div>
              ) : isTestnet ? (
                <div
                  onClick={handleGetSome}
                  style={{
                    fontFamily: MONO,
                    fontSize: 10,
                    letterSpacing: "0.04em",
                    color: "#A5A5A0",
                    cursor: "pointer",
                    minWidth: 52,
                    textAlign: "right",
                  }}
                >
                  Get some
                </div>
              ) : null}
            </div>
          </div>
        ))}

        <div style={{ height: 8 }} />
      </div>
    </div>
  );
}
